// src/components/ViewerContainer.js
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

import { useReflection } from "../contexts/ReflectionContext"; // 新增导入
import ReflectionPanel from "./ReflectionPanel"; // 新增导入

import LaserCutInterlocking from "../pages/LaserCutInterlocking";
import WireMeshGenerator from "../pages/WireMeshGenerator";
import StackedLaserCutLayers from "../pages/StackedLaserCutLayers";
import GCodeGenerator from "../pages/GCodeGenerator"; // 添加这一行
import MoldMaking from "../pages/MoldMaking"; // 新增模具制作组件
import WireCopperElectroplating from "../pages/WireCopperElectroplating"; // 新增铜电镀组件
import HotWireFoamCutting from "../pages/HotWireFoamCutting";
import PaperUnfolding from "../pages/PaperUnfolding"; // 新增纸张展开组件
import EpoxyCoating from "../pages/EpoxyCoating";
import NeedleFeltSculpture from "../pages/NeedleFeltSculpture";
import EscapeLoom from "../pages/EscapeLoom";
import LaserCutClaySculpture from "../pages/LaserCutClaySculpture";
import PaperMacheShaping from "../pages/PaperMacheShaping";
import StackMold from "../pages/StackMold";
import SiliconeMold from "../pages/SiliconeMold";
import BambooAgent from "../pages/BambooAgent";
import WireMeshGenerator2 from "../pages/WireMeshSculpture2";
import StackedLaserCutLayers2 from "../pages/StackedLaserCutLayers2";
import LaserCutInterlockingPlanar2 from "../pages/LaserCutInterlocking2";
//import FingerJoint from "../pages/FingerJoints";

import "../styles/global.css";
import FingerJointApp from "../pages/FingerJoints";

import Example1FloatingPart from "../examples/Example1FloatingPart";
import Example2SupportProblem from "../examples/Example2SupportProblem";
import Example3SizeLimitation from "../examples/Example3SizeLimitation";
//import NodeEditorPage from "../pages/NodeEditorPage";

// Environment configuration
const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";

const ViewerContainer = () => {
  const [openViews, setOpenViews] = useState(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [selectedSTLFile, setSelectedSTLFile] = useState("");
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateItem, setDuplicateItem] = useState("");
  const location = useLocation();

  const { showReflection, currentWorkflows, openReflection, closeReflection } =
    useReflection(); // 新增

  // Refs for tracking drag operations
  const draggedItemsRef = useRef(new Set());
  const dragCountRef = useRef(0);

  // Get STL file from URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const modelParam = queryParams.get("model");
    const modelNameParam = queryParams.get("modelName");

    // First check if we have a model name parameter (new approach)
    if (modelNameParam) {
      // Try to get the model from localStorage first
      const storedPath = localStorage.getItem("selectedSTLModel");
      const storedName = localStorage.getItem("selectedSTLModelName");

      if (storedPath && storedName === modelNameParam) {
        // Use the stored blob URL directly
        setSelectedSTLFile(storedPath);
      } else {
        // If not in localStorage, fetch from server by filename
        fetchModelFromServer(modelNameParam);
      }
    }
    // Fallback to the old approach if modelName isn't available
    else if (modelParam) {
      if (modelParam.startsWith("blob:") || modelParam.startsWith("http")) {
        setSelectedSTLFile(modelParam);
      } else {
        fetchModelFromServer(modelParam);
      }
    }
  }, [location]);

  // Fetch model from server if needed
  const fetchModelFromServer = async (filename) => {
    try {
      // Check if the filename already includes UUID pattern (contains underscore followed by 8 hex chars)
      const hasUUID = /.*_[0-9a-f]{8}\.[a-z]+$/i.test(filename);

      // If it already looks like a full URL, use it directly
      if (filename.startsWith("http://") || filename.startsWith("https://")) {
        console.log("Using direct URL:", filename);
        setSelectedSTLFile(filename);
        return;
      }

      // If it's a filename with UUID, try direct GCS URL first
      if (hasUUID) {
        const gcsUrl = `https://storage.googleapis.com/cameleon-models/${filename}`;
        console.log("Trying direct GCS URL with UUID:", gcsUrl);

        try {
          const gcsResponse = await fetch(gcsUrl, { method: "HEAD" });
          if (gcsResponse.ok) {
            console.log("GCS URL with UUID works, using it directly");
            setSelectedSTLFile(gcsUrl);
            return;
          }
        } catch (gcsError) {
          console.error("Error checking GCS URL with UUID:", gcsError);
        }
      }

      // Try the backend route
      const response = await fetch(`${API_URL}/download/${filename}`);

      // Handle redirects (for GCS URLs)
      if (response.redirected) {
        console.log("Redirected to storage URL:", response.url);
        setSelectedSTLFile(response.url);
        return;
      }

      if (response.ok) {
        const blob = await response.blob();
        const fileURL = URL.createObjectURL(blob);
        setSelectedSTLFile(fileURL);
      } else {
        console.error("Failed to fetch model file:", response.statusText);

        // Last resort - try standard GCS URL without UUID
        // This is a fallback for older references that might not have UUID
        const gcsUrl = `https://storage.googleapis.com/cameleon-models/${filename}`;
        console.log("Trying standard GCS fallback URL:", gcsUrl);

        try {
          const gcsResponse = await fetch(gcsUrl, { method: "HEAD" });
          if (gcsResponse.ok) {
            setSelectedSTLFile(gcsUrl);
          } else {
            console.error(
              "Model not found at GCS URL either:",
              gcsResponse.statusText
            );
          }
        } catch (gcsError) {
          console.error("Error checking GCS URL:", gcsError);
        }
      }
    } catch (error) {
      console.error("Error fetching model:", error);
    }
  };

  // Handle drag enter - increment counter to handle multiple drag entries and exits
  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      dragCountRef.current++;

      if (!isDragging) {
        setIsDragging(true);
        e.currentTarget.classList.add("dragover");
      }
    },
    [isDragging]
  );

  // Handle drag leave - decrement counter and only clear dragging if at 0
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    dragCountRef.current--;

    if (dragCountRef.current === 0) {
      setIsDragging(false);
      e.currentTarget.classList.remove("dragover");
    }
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Ensure dragover class is applied
    if (!e.currentTarget.classList.contains("dragover")) {
      e.currentTarget.classList.add("dragover");
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset drag counters and flags
    dragCountRef.current = 0;
    setIsDragging(false);
    e.currentTarget.classList.remove("dragover");

    // Get the current dragged items
    const workflowTypes = Array.from(draggedItemsRef.current);
    draggedItemsRef.current.clear(); // Clear the set for next use

    if (workflowTypes.length === 0) {
      console.warn("No items found in drop event");
      return;
    }

    setOpenViews((prev) => {
      const newViews = new Map(prev);

      // Process each dragged item
      for (const workflowType of workflowTypes) {
        // Check if view already exists
        /*  if (newViews.has(workflowType)) {
          setDuplicateItem(workflowType.split("_").pop());
          setShowDuplicateWarning(true);
          setTimeout(() => setShowDuplicateWarning(false), 3000);
          continue;
        } */

        // Check if max limit reached
        if (newViews.size >= 4) {
          alert(
            "You can only add up to 4 items. Please remove an item before adding a new one."
          );
          break;
        }

        // Add new view
        // Add new view with unique key
        const uniqueKey = `${workflowType}_${Date.now()}_${Math.random()}`;
        newViews.set(uniqueKey, {
          id: Date.now() + Math.random(),
          type: workflowType,
          position: newViews.size,
        });
      }

      return newViews;
    });
  }, []);

  // Add to dragged items during drag start
  const addToDraggedItems = useCallback((workflowType) => {
    draggedItemsRef.current.add(workflowType);
  }, []);

  // Expose the method via window for external components to use
  useEffect(() => {
    window.addToDraggedItems = addToDraggedItems;

    return () => {
      delete window.addToDraggedItems;
    };
  }, [addToDraggedItems]);

  const removeView = useCallback((workflowType) => {
    setOpenViews((prev) => {
      const newViews = new Map(prev);
      newViews.delete(workflowType);

      // Reassign positions to maintain order
      let position = 0;
      const updatedViews = new Map();

      Array.from(newViews.entries()).forEach(([key, value]) => {
        updatedViews.set(key, {
          ...value,
          position: position++,
        });
      });

      return updatedViews;
    });
  }, []);

  // Memoize workflow rendering function to optimize re-renders
  // 在 ViewerContainer.js 的 renderWorkflowView 函数中更新 switch 语句

  const renderWorkflowView = useMemo(() => {
    return (type) => {
      const stlFile = selectedSTLFile || "/models/default.stl";

      switch (type) {
        case "FoundationalWorkflows_Laser cut Interlocking planar (Algorithm 2)":
          return (
            <LaserCutInterlocking
              key={`laser-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Wire mesh sculpture (Algorithm 2)":
          return (
            <WireMeshGenerator key={`wire-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_Wire Mesh Sculpture (Algorithm 1)":
          return (
            <WireMeshGenerator2
              key={`wireMesh2-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Laser cut stacked sheets (Algorithm 2)":
          return (
            <StackedLaserCutLayers
              key={`stacked-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_GCode Generator":
          return (
            <GCodeGenerator key={`gcode-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_Paper unfolding":
          return (
            <PaperUnfolding key={`paper-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_Mold making":
          return <MoldMaking key={`mold-${Date.now()}`} stlFile={stlFile} />;
        case "FoundationalWorkflows_Hot Wire Foam Cutting":
          return (
            <HotWireFoamCutting
              key={`hotwirefoam-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Wire copper electroplating":
          return (
            <WireCopperElectroplating
              key={`copper-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Epoxy coating":
          return <EpoxyCoating key={`epoxy-${Date.now()}`} stlFile={stlFile} />;
        case "FoundationalWorkflows_Needle felt sculpture":
          return (
            <NeedleFeltSculpture
              key={`feltSculpture-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Escape Loom":
          return (
            <EscapeLoom key={`escapeLoom-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_Laser cut clay sculpture":
          return (
            <LaserCutClaySculpture
              key={`claySculpture-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Paper mache shaping":
          return (
            <PaperMacheShaping
              key={`paperMache-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_StackMold":
          return (
            <StackMold key={`stackMold-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_Silcone mold":
          return (
            <SiliconeMold key={`silicone-${Date.now()}`} stlFile={stlFile} />
          );
        case "FoundationalWorkflows_bamboo agent":
          return <BambooAgent key={`bamboo-${Date.now()}`} stlFile={stlFile} />;

        case "FoundationalWorkflows_Laser cut Interlocking planar (Algorithm 1)":
          return (
            <LaserCutInterlockingPlanar2
              key={`interlockPlanar2-${Date.now()}`}
              stlFile={stlFile}
            />
          );
        case "FoundationalWorkflows_Laser cut stacked sheets (Algorithm 1)":
          return (
            <StackedLaserCutLayers2
              key={`laserStacked2-${Date.now()}`}
              stlFile={stlFile}
            />
          );


        // Learning Examples：
        case "Example1FloatingPart":
          return (
            <Example1FloatingPart
              key={`example1-${Date.now()}`}
              stlFile="/models/twistT.stl"
            />
          );
        case "Example2SupportProblem":
          return (
            <Example2SupportProblem
              key={`example2-${Date.now()}`}
              stlFile="/models/twistT.stl"
            />
          );
        case "Example3SizeLimitation":
          return (
            <Example3SizeLimitation
              key={`example3-${Date.now()}`}
              stlFile="/models/twistT.stl"
            />
          );
        default:
          return <div>Unknown workflow type</div>;
      }
    };
  }, [selectedSTLFile]);

  // Function to determine grid layout styles based on number of views
  const getGridStyles = useMemo(() => {
    const viewCount = openViews.size;

    if (viewCount === 0) {
      return {
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
      };
    } else if (viewCount === 1) {
      return {
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
      };
    } else if (viewCount === 2) {
      return {
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr",
      };
    } else {
      // For 3 or 4 items: always show 2x2 grid
      return {
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
      };
    }
  }, [openViews.size]);

  // Function to get grid position for a specific view
  const getItemStyle = useCallback(
    (position) => {
      const viewCount = openViews.size;

      // For 3 items, we want to leave the bottom-right cell empty
      if (viewCount === 3 && position === 2) {
        return {
          gridColumn: "1",
          gridRow: "2",
        };
      }

      return {};
    },
    [openViews.size]
  );

  // Sort views by position for consistent ordering
  const sortedViews = useMemo(() => {
    return Array.from(openViews.entries()).sort(
      (a, b) => a[1].position - b[1].position
    );
  }, [openViews]);

  // 新增：获取当前打开工作流的名称
  const getCurrentWorkflowNames = useCallback(() => {
    return Array.from(openViews.values()).map((view) => {
      // 从文件名转换为显示名称
      const name = view.type.split("_").slice(1).join(" / ");

      // 处理算法版本命名
      const algorithmMappings = {
        "Laser cut Interlocking planar2":
          "Laser cut Interlocking planar (Algorithm 2)",
        "Laser cut stacked sheets2": "Laser cut stacked sheets (Algorithm 2)",

        "Laser cut Interlocking planar":
          "Laser cut Interlocking planar (Algorithm 1)",
        "Laser cut stacked sheets": "Laser cut stacked sheets (Algorithm 1)",
        "Wire mesh sculpture": "Wire mesh sculpture (Algorithm 2)",
        "Wire Mesh Sculpture2": "Wire Mesh Sculpture (Algorithm 1)",
        
      };

      return algorithmMappings[name] || name;
    });
  }, [openViews]);

  // 新增：处理反思按钮点击
  const handleReflectionClick = useCallback(() => {
    const workflowNames = getCurrentWorkflowNames();
    openReflection(workflowNames);
  }, [getCurrentWorkflowNames, openReflection]);

  return (
    <div
      id="viewer-container"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      style={{
        display: "grid",
        gridTemplateColumns: getGridStyles.gridTemplateColumns,
        gridTemplateRows: getGridStyles.gridTemplateRows,
        gap: "10px",
        padding: "10px",
        height: "100%",
        position: "relative",
      }}
    >
      <div
        id="drag-overlay"
        style={{
          display: isDragging ? "block" : "none",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.1)",
          zIndex: 1000,
        }}
      />

      {/* 新增：全局反思按钮 - 添加这整个部分 */}
      {openViews.size > 0 && (
        <button
          className="panel-toggle-button"
          onClick={handleReflectionClick}
          style={{
            position: "absolute",
            top: "80px",
            right: "20px",
            zIndex: 100,
          }}
        >
          ⟩ My Reflection
        </button>
      )}

      {openViews.size === 0 && (
        <div
          id="drag-drop-message"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#666",
            fontSize: "1.1em",
            textAlign: "center",
          }}
        >
          {selectedSTLFile
            ? "STL model loaded. Drag and drop workflow items here to view them."
            : "Drag and drop workflow items here to view them"}
        </div>
      )}

      {sortedViews.map(([uniqueKey, { id, position, type }]) => (
        <div
          key={id}
          className="item-wrapper"
          style={{
            position: "relative",
            height: "100%",
            backgroundColor: "#fff",
            borderRadius: "8px", // 稍微增加圆角
            overflow: "hidden",
            border: "2px solid #dee2e6", // 添加边框
            boxShadow: "0 8px 8px rgba(0, 0, 0, 0.08)", // 添加轻微阴影
            transition: "all 0.2s ease", // 添加过渡效果
            ...getItemStyle(position),
          }}
        >
          {renderWorkflowView(type)}
          <button
            className="close-button"
            onClick={() => removeView(uniqueKey)} // 传递uniqueKey而不是type
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "24px",
              height: "24px",
              borderRadius: "12px",
              backgroundColor: "grey",
              border: "1px solid #ddd",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              lineHeight: 1,
              padding: 0,
              zIndex: 100,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            ×
          </button>
        </div>
      ))}

      {/* Empty placeholder cell for 3 windows layout */}
      {openViews.size === 3 && (
        <div
          className="empty-placeholder"
          style={{
            gridColumn: "2",
            gridRow: "2",
            backgroundColor: "#f8f8f8",
            borderRadius: "4px",
            border: "1px dashed #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#aaa",
          }}
        >
          <div>Drag a workflow here</div>
        </div>
      )}

      {/* Duplicate item warning */}
      {showDuplicateWarning && (
        <div
          className="duplicate-warning"
          style={{
            position: "absolute",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ffeb3b",
            color: "#333",
            padding: "10px 15px",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            zIndex: 1001,
            maxWidth: "90%",
            textAlign: "center",
            animation: "fadeInOut 3s ease-in-out",
          }}
        >
          <strong>{duplicateItem}</strong> is already in the viewer. Duplicate
          items are not allowed.
        </div>
      )}

      {/* 新增：全局反思面板 - 在最后添加这个 */}
      {showReflection && (
        <ReflectionPanel
          workflowNames={currentWorkflows} // 传递所有当前工作流
          onClose={closeReflection}
        />
      )}
    </div>
  );
};

export default ViewerContainer;
